import { isAnnotationBundleSet, type AnnotationSetSummaryView } from "@/api/trainingAnnotation";

/**
 * 학습 입력으로 쓸 수 있는 어노테이션 세트를 구분하는 판정.
 *
 * 선택 가능 조건은 BE 게이트(`submit/annotation_binding.py`)와 같다. **committed 이고
 * materialize 가 끝나 경로가 기록된 세트**만 고를 수 있다. 나머지는 숨기지 않고 사유와 함께
 * 잠근다. 방금 만든 세트가 목록에 없으면 사용자는 고를 수 없는 이유를 알 수 없다.
 *
 * 여기서 미리 거르지 않으면 확정 단계에서 서버 404/409(`annotation_set_not_committed` ·
 * `annotation_set_not_materialized`) 오류가 난다.
 */

// 카드가 사용하는 후보 원본. 적재 상태와 입력별 목록이다. 컨트롤러가 만들고 카드가 읽는다.
// 상태를 함께 담는 이유는, 세트가 0건인 경우와 아직 못 받은 경우를 카드가 구별해야 하기 때문이다
// (0건은 "만들기"를 권하고, 못 받음은 기다리라고 표시한다).
export type AnnotationSetsSource = {
  status: "idle" | "loading" | "success" | "error";
  byDataset: ReadonlyMap<string, readonly AnnotationSetSummaryView[]>;
};

// 후보 한 개. 드롭다운 항목으로 그대로 옮길 수 있는 형태다(렌더는 카드가 소유한다).
export type AnnotationSetChoice = {
  uuid: string;
  label: string;
  // 고를 수 있으면 규모 요약, 고를 수 없으면 사유. 항목의 설명 줄에 그대로 표시한다.
  detail: string | null;
  selectable: boolean;
};

// 사용자는 이름을 비워 둘 수 있다. 그때도 목록에서 서로 구분되도록 uuid 앞부분을 쓴다.
export function annotationSetLabel(set: AnnotationSetSummaryView): string {
  const name = set.name?.trim() ?? "";
  return name === "" ? `이름 없는 세트 (${set.uuid.slice(0, 8)})` : name;
}

/**
 * 고를 수 없는 사유. 고를 수 있으면 null.
 *
 * 판정 기준은 `materialized_path` 다(잡 상태가 아니다). 경로가 곧 training 이 읽을 폴더이고,
 * BE 게이트도 그 값으로 409 를 준다. 잡 상태는 경로가 아직 없는 이유를 설명할 때만 쓴다.
 */
export function annotationSetBlockReason(set: AnnotationSetSummaryView): string | null {
  if (set.status !== "committed") {
    return "commit 이 필요합니다";
  }
  if (set.materialized_path !== null) {
    return null;
  }
  const job = set.materialize;
  if (job === null) {
    return "렌더가 필요합니다";
  }
  switch (job.status) {
    case "queued":
      return "렌더 대기 중입니다";
    case "running":
      return `렌더 중입니다 (${job.rendered_count}/${job.requested_count})`;
    case "failed":
      return "렌더가 실패했습니다";
    case "succeeded":
      // 잡은 끝났지만 경로가 없는 상태. 쓸 수 없으므로 잠그고 사실만 표시한다.
      return "렌더 결과가 아직 기록되지 않았습니다";
  }
}

export function isAnnotationSetSelectable(set: AnnotationSetSummaryView): boolean {
  return annotationSetBlockReason(set) === null;
}

// 서버가 준 순서를 유지한다. 목록 화면(스튜디오)과 순서가 같아야 방금 만든 세트를 같은 자리에서 찾는다.
export function toAnnotationSetChoices(
  sets: readonly AnnotationSetSummaryView[],
): AnnotationSetChoice[] {
  return sets.map((set) => {
    const reason = annotationSetBlockReason(set);
    return {
      uuid: set.uuid,
      label: annotationSetLabel(set),
      detail: reason ?? keyframeSummary(set),
      selectable: reason === null,
    };
  });
}

// 규모는 렌더한 keyframe 수로 표시한다. 프롬프트 항목 수가 아니라 keyframe 이 mask 생성 단위다.
function keyframeSummary(set: AnnotationSetSummaryView): string | null {
  const rendered = isAnnotationBundleSet(set)
    ? (set.bundle?.keyframe_count ?? null)
    : (set.materialize?.rendered_count ?? null);
  return rendered === null ? null : `keyframe ${rendered}개`;
}
